/**
 * 音声の一部区間 [start, end) をサンプル精度で鳴らすプレイヤー (Web Audio)。
 *
 * `<audio>` の currentTime を requestAnimationFrame で監視して pause() する方式では、境界を数十ms超えてから
 * 止まる (フレーム間隔+currentTime の更新粒度+pause の遅延) ため、区間再生で次の区間の頭が鳴ってしまう。
 * AudioBufferSourceNode.start(when, offset, duration) はサンプル精度で区間を鳴らし、タブが裏に回っても
 * (rAF が止まっても) 境界で止まる。
 *
 * ナレーション分割位置の調整 (scenario/NarrationSplitEditorModal) と、波形トリム部品 (AudioRangeTrimmer)
 * が使う。音声バイトは呼び出し側が渡す (GCS の CORS 事情は fetchMaterialContentBlob が吸収する)。
 * デコード済みの AudioBuffer を直接渡してもよい (波形のピーク計算と同じデコード結果を使い回すため)。
 */

export type RangePlayback = {
  start: number;
  /** null は最後まで */
  end: number | null;
};

export type PlayResult = 'started' | 'superseded' | 'unavailable';

/** 再生中に掛ける音量の折れ線 (音源秒 → 0〜1)。点の外側は端の値、点の間は直線 (GainNode の自動化)。 */
export type GainEnvelopePoint = { sec: number; gain: number };

export interface PlayOptions {
  /** 区間ごとの音量 (区間ミュート / 減音) の試聴用。省略で等倍。 */
  gainEnvelope?: readonly GainEnvelopePoint[];
}

function envelopeGainAt(points: readonly GainEnvelopePoint[], sec: number): number {
  if (points.length === 0) return 1;
  if (sec <= points[0]!.sec) return points[0]!.gain;
  const last = points[points.length - 1]!;
  if (sec >= last.sec) return last.gain;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1]!;
    const next = points[i]!;
    if (sec <= next.sec) {
      const span = next.sec - prev.sec;
      if (span <= 0) return next.gain;
      return prev.gain + ((next.gain - prev.gain) * (sec - prev.sec)) / span;
    }
  }
  return last.gain;
}

type Active = RangePlayback & {
  source: AudioBufferSourceNode;
  /** 音量の自動化に使う GainNode (無ければ destination へ直結) */
  gain: GainNode | null;
  /** 再生を始めた時点の AudioContext.currentTime */
  startedAt: number;
};

export interface RangeAudioPlayerOptions {
  /**
   * 使う AudioContext。渡すと dispose() でも close しない (共有の文脈: sharedAudioContext.ts)。
   * 省略すると自前で作り、dispose() で close する。
   */
  context?: AudioContext;
}

function isAudioBuffer(value: ArrayBuffer | AudioBuffer): value is AudioBuffer {
  return typeof (value as AudioBuffer).getChannelData === 'function';
}

export class RangeAudioPlayer {
  private ctx: AudioContext | null = null;
  private readonly ownsContext: boolean;
  private buffer: AudioBuffer | null = null;
  private loading: Promise<AudioBuffer | null> | null = null;
  private active: Active | null = null;
  private disposed = false;
  /** play() の呼び出し世代。デコード待ちの間に次の play()/stop() が来たら古い方は始めない */
  private generation = 0;

  private readonly loadBytes: () => Promise<ArrayBuffer | AudioBuffer>;

  constructor(loadBytes: () => Promise<ArrayBuffer | AudioBuffer>, options?: RangeAudioPlayerOptions) {
    this.loadBytes = loadBytes;
    this.ctx = options?.context ?? null;
    this.ownsContext = !options?.context;
  }

  static isSupported(): boolean {
    return typeof AudioContext !== 'undefined';
  }

  private getContext(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext();
    return this.ctx;
  }

  /** 音声を取得してデコードする (1 回だけ)。開いた時点で呼んでおくと初回再生の待ちが消える */
  preload(): Promise<AudioBuffer | null> {
    if (this.buffer) return Promise.resolve(this.buffer);
    if (!this.loading) {
      this.loading = (async () => {
        try {
          const loaded = await this.loadBytes();
          if (this.disposed) return null;
          // decodeAudioData は ArrayBuffer を detach するので、呼び出し側は再利用しないこと
          const decoded = isAudioBuffer(loaded) ? loaded : await this.getContext().decodeAudioData(loaded);
          if (this.disposed) return null;
          this.buffer = decoded;
          return decoded;
        } catch (error) {
          console.warn('[RangeAudioPlayer] 試聴用の音声をデコードできませんでした', error);
          this.loading = null;
          return null;
        }
      })();
    }
    return this.loading;
  }

  /** デコード済み音声の長さ (秒)。未デコードなら null */
  get bufferDuration(): number | null {
    return this.buffer?.duration ?? null;
  }

  /**
   * [start, end) を再生する。再生中のものは止める。
   * 区間を鳴らし終えたとき (stop() で止めたときは呼ばない) に onEnded を呼ぶ。
   * 'superseded' はデコード待ちの間に次の play()/stop() が来て、こちらは始めなかったとき
   */
  async play(
    start: number,
    end: number | null,
    onEnded: () => void,
    options?: PlayOptions,
  ): Promise<PlayResult> {
    this.stop();
    const gen = ++this.generation;
    const buffer = await this.preload();
    if (this.disposed) return 'unavailable';
    if (gen !== this.generation) return 'superseded';
    if (!buffer) return 'unavailable';
    const ctx = this.getContext();
    // Safari は利用者の操作の中で resume しないと鳴らない。クリックから呼ばれる前提
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        return 'unavailable';
      }
      if (this.disposed) return 'unavailable';
      if (gen !== this.generation) return 'superseded';
    }
    const offset = Math.min(Math.max(0, start), buffer.duration);
    const duration = end === null ? undefined : Math.max(0, Math.min(end, buffer.duration) - offset);
    if (duration !== undefined && duration <= 0) {
      // 鳴らすものがない。呼び出し側が「再生中」にした後で終わらせるため、同期ではなく次のタスクで知らせる
      setTimeout(onEnded, 0);
      return 'started';
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    // 音量の折れ線 (区間ごとの音量の試聴) があれば GainNode を挟み、再生開始時刻を基準に自動化する。
    // AudioParam の自動化はサンプル精度なので、rAF で volume を書き換えるより境界が正確になる
    const envelope = options?.gainEnvelope;
    let gain: GainNode | null = null;
    const now = ctx.currentTime;
    if (envelope && envelope.length > 0) {
      gain = ctx.createGain();
      const limit = end ?? buffer.duration;
      gain.gain.setValueAtTime(envelopeGainAt(envelope, offset), now);
      for (const point of envelope) {
        if (point.sec <= offset || point.sec > limit) continue;
        gain.gain.linearRampToValueAtTime(point.gain, now + (point.sec - offset));
      }
      source.connect(gain);
      gain.connect(ctx.destination);
    } else {
      source.connect(ctx.destination);
    }
    const active: Active = { start: offset, end, source, gain, startedAt: now };
    source.onended = () => {
      // stop() で止めたときは active が差し替わっている
      if (this.active !== active) return;
      this.active = null;
      source.disconnect();
      gain?.disconnect();
      onEnded();
    };
    this.active = active;
    source.start(0, offset, duration);
    return 'started';
  }

  /** 今鳴らしている区間。止まっていれば null */
  get playing(): RangePlayback | null {
    return this.active ? { start: this.active.start, end: this.active.end } : null;
  }

  /** 再生位置 (秒)。止まっていれば null */
  position(): number | null {
    const a = this.active;
    if (!a || !this.ctx) return null;
    const t = a.start + (this.ctx.currentTime - a.startedAt);
    const limit = a.end ?? this.buffer?.duration ?? t;
    return Math.min(t, limit);
  }

  stop(): void {
    this.generation++;
    const a = this.active;
    if (!a) return;
    this.active = null;
    a.source.onended = null;
    try {
      a.source.stop();
    } catch {
      // すでに止まっている
    }
    a.source.disconnect();
    a.gain?.disconnect();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.buffer = null;
    this.loading = null;
    if (this.ownsContext) void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
  }
}
